"use client";

import { RefineThemes } from "@refinedev/antd";
import { App as AntdApp, ConfigProvider, theme } from "antd";
import { colors, fontFamily } from "../../styles/tokens";
import Cookies from "js-cookie";
import React, {
  createContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from "react";

type ColorModeContextType = {
  mode: string;
  setMode: (mode: string) => void;
};

export const ColorModeContext = createContext<ColorModeContextType>(
  {} as ColorModeContextType
);

type ColorModeContextProviderProps = {
  defaultMode?: string;
};

export const ColorModeContextProvider: React.FC<
  PropsWithChildren<ColorModeContextProviderProps>
> = ({ children, defaultMode }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [mode, setMode] = useState(defaultMode || "light");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      const theme = Cookies.get("theme") || "light";
      setMode(theme);
    }
  }, [isMounted]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const setColorMode = () => {
    if (mode === "light") {
      setMode("dark");
      Cookies.set("theme", "dark");
    } else {
      setMode("light");
      Cookies.set("theme", "light");
    }
  };

  const { darkAlgorithm, defaultAlgorithm } = theme;

  // Custom Stripe-inspired theme
  const customTheme = {
    token: {
      colorPrimary: colors.primary,
      colorSuccess: colors.success,
      colorWarning: colors.warning,
      colorError: colors.danger,
      colorInfo: colors.primary,
      colorLink: colors.primary,
      colorTextBase: colors.text,
      borderRadius: 8,
      fontFamily,
    },
    components: {
      Button: {
        primaryColor: "#ffffff",
        colorPrimaryHover: colors.primaryHover,
        colorPrimaryActive: colors.primaryHover,
      },
      Segmented: {
        itemSelectedBg: "#ffffff",
        itemSelectedColor: colors.text,
        trackBg: colors.neutralSoft,
      },
      Switch: {
        colorPrimary: colors.primary,
        colorPrimaryHover: colors.primaryHover,
      },
      Tabs: {
        inkBarColor: "#6366f1",
        itemSelectedColor: "#6366f1",
        itemHoverColor: "#818cf8",
        itemColor: "rgba(0, 0, 0, 0.65)",
      },
      Menu: {
        itemSelectedBg: "rgba(99, 102, 241, 0.1)",
        itemSelectedColor: "#6366f1",
        itemHoverBg: "rgba(99, 102, 241, 0.05)",
        itemHoverColor: "#6366f1",
      },
      Input: {
        activeBorderColor: "#6366f1",
        hoverBorderColor: "#818cf8",
      },
      Select: {
        optionSelectedBg: "rgba(99, 102, 241, 0.1)",
        optionSelectedColor: "#6366f1",
      },
    },
  };

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ConfigProvider
        theme={{
          ...customTheme,
          algorithm: mode === "light" ? defaultAlgorithm : darkAlgorithm,
        }}
      >
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ColorModeContext.Provider>
  );
};
